import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  BadgeCheck, Clock, XCircle, CheckCircle, User, ChevronRight, Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const POSE_NAMES = {
  peace_sign: 'Peace Sign',
  thumbs_up: 'Thumbs Up',
  wave: 'Wave',
  pointing: 'Pointing at Chin'
};

const AdminVerifications = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/verifications?status=${statusFilter}`);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (request) => {
    try {
      const response = await axios.get(`${API}/verifications/${request.id}`);
      setSelectedRequest(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    
    try {
      await axios.post(`${API}/verifications/${selectedRequest.request.id}/approve`);
      toast.success('Verification approved');
      setShowDetailModal(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    
    try {
      await axios.post(`${API}/verifications/${selectedRequest.request.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Verification rejected');
      setShowDetailModal(false);
      setRejectionReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded text-xs">Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-[#22c55e]/10 text-[#22c55e] rounded text-xs">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-xs">Rejected</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Profile Verifications</h1>
        <p className="text-[#A1A1AA]">Review and approve user verification requests</p>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected'].map(status => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setLoading(true); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === status 
                ? 'bg-[#FF2E63] text-white' 
                : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-yellow-500" />
            <span className="text-[#A1A1AA]">Pending</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {requests.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-5 h-5 text-[#22c55e]" />
            <span className="text-[#A1A1AA]">Approved</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {statusFilter === 'approved' ? requests.length : '—'}
          </p>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-[#A1A1AA]">Rejected</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {statusFilter === 'rejected' ? requests.length : '—'}
          </p>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-[#111] border border-white/10 rounded-xl">
        {requests.length === 0 ? (
          <div className="p-8 text-center">
            <BadgeCheck className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No {statusFilter} verification requests</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {requests.map(request => (
              <div
                key={request.id}
                className="p-4 hover:bg-white/5 transition-colors cursor-pointer flex items-center gap-4"
                onClick={() => handleViewDetail(request)}
              >
                {/* User Photo */}
                <div className="w-12 h-12 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                  {request.user?.public_photos?.[0] ? (
                    <img 
                      src={request.user.public_photos[0].startsWith('data:') 
                        ? request.user.public_photos[0] 
                        : `data:image/jpeg;base64,${request.user.public_photos[0]}`}
                      alt={request.user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-6 h-6 text-[#52525B]" />
                    </div>
                  )}
                </div>
                
                {/* Request Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">
                      {request.user?.display_name || request.user?.username || 'Unknown User'}
                    </p>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-sm text-[#A1A1AA]">
                    Pose: {POSE_NAMES[request.pose_type] || request.pose_type}
                  </p>
                  <p className="text-xs text-[#52525B]">
                    Submitted: {formatDate(request.submitted_at)}
                  </p>
                </div>

                {/* Selfie Preview */}
                <div className="w-16 h-16 rounded bg-white/10 overflow-hidden flex-shrink-0">
                  {request.selfie_data ? (
                    <img 
                      src={request.selfie_data.startsWith('data:') 
                        ? request.selfie_data 
                        : `data:image/jpeg;base64,${request.selfie_data}`}
                      alt="Verification selfie"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-6 h-6 text-[#52525B]" />
                    </div>
                  )}
                </div>

                <ChevronRight className="w-5 h-5 text-[#52525B]" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Verification Request</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="py-4 space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/10 overflow-hidden">
                  {selectedRequest.user?.public_photos?.[0] ? (
                    <img 
                      src={selectedRequest.user.public_photos[0].startsWith('data:') 
                        ? selectedRequest.user.public_photos[0] 
                        : `data:image/jpeg;base64,${selectedRequest.user.public_photos[0]}`}
                      alt={selectedRequest.user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-8 h-8 text-[#52525B]" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-white">
                    {selectedRequest.user?.display_name || selectedRequest.user?.username}
                  </p>
                  <p className="text-[#A1A1AA]">@{selectedRequest.user?.username}</p>
                  <p className="text-sm text-[#52525B]">
                    Member since {new Date(selectedRequest.user?.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Verification Selfie */}
              <div className="space-y-2">
                <p className="text-[#A1A1AA] text-sm">
                  Required Pose: <span className="text-white font-medium">{POSE_NAMES[selectedRequest.request.pose_type]}</span>
                </p>
                <div className="bg-white/5 rounded-xl overflow-hidden">
                  <img 
                    src={selectedRequest.request.selfie_data?.startsWith('data:') 
                      ? selectedRequest.request.selfie_data 
                      : `data:image/jpeg;base64,${selectedRequest.request.selfie_data}`}
                    alt="Verification selfie"
                    className="w-full max-h-96 object-contain"
                  />
                </div>
              </div>

              {/* User's Profile Photos for Comparison */}
              {selectedRequest.user?.public_photos?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[#A1A1AA] text-sm">Profile Photos (for comparison)</p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {selectedRequest.user.public_photos.slice(0, 5).map((photo, i) => (
                      <img 
                        key={i}
                        src={photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`}
                        alt={`Profile ${i+1}`}
                        className="w-20 h-20 rounded object-cover flex-shrink-0"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection Reason (if pending) */}
              {selectedRequest.request.status === 'pending' && (
                <div className="space-y-2">
                  <p className="text-[#A1A1AA] text-sm">Rejection Reason (required if rejecting)</p>
                  <Input
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Face doesn't match, pose incorrect, etc."
                    className="bg-white/5 border-white/10"
                  />
                </div>
              )}

              {/* Already Processed Info */}
              {selectedRequest.request.status !== 'pending' && (
                <div className={`p-4 rounded-lg ${
                  selectedRequest.request.status === 'approved' 
                    ? 'bg-[#22c55e]/10 border border-[#22c55e]/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className="font-medium">
                    {selectedRequest.request.status === 'approved' ? 'Approved' : 'Rejected'}
                  </p>
                  <p className="text-sm text-[#A1A1AA]">
                    on {formatDate(selectedRequest.request.reviewed_at)}
                  </p>
                  {selectedRequest.request.rejection_reason && (
                    <p className="text-sm mt-2">Reason: {selectedRequest.request.rejection_reason}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedRequest?.request.status === 'pending' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleReject}
                  disabled={processing}
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  onClick={handleApprove}
                  disabled={processing}
                  className="bg-[#22c55e] hover:bg-[#16a34a]"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {selectedRequest?.request.status !== 'pending' && (
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVerifications;
